import { useTranslation } from '@pancakeswap/localization'
import { Box, Card, Flex, NextLinkFromReactRouter, Skeleton, Text } from '@pancakeswap/uikit'
import { fromUnixTime } from 'date-fns'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import useSWR from 'swr'
import BarChart from 'views/Info/components/InfoCharts/BarChart'
import LineChart from 'views/Info/components/InfoCharts/LineChart'
import { formatAmount } from 'utils/formatInfoNumbers'
import type { HistoryOut, Range, SeriesOut } from 'server/info/history'
import type { DexVersion } from 'server/info/types'

/**
 * TVL and Volume as two separate charts with a time range (7D, 1M, 3M, 1Y, All), the protocol (V2, V3, both) where it can change, for the whole DEX, one pool or
 * one token. Every series comes from /api/info/history: a chart is the sum of the pools whose own history agrees with the chain, and the card says in one line
 * how many pools are left out, with the reason for each one behind it. A pool that is not trusted is never in a sum; with no verified pool the card says
 * "not available", why, and what the chain holds now.
 */
const RANGES: Array<[Range, string]> = [['7d', '7D'], ['1m', '1M'], ['3m', '3M'], ['1y', '1Y'], ['all', 'All']]
const PROTOCOLS: Array<[DexVersion | 'all', string]> = [['all', 'All'], ['v2', 'V2'], ['v3', 'V3']]

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  justify-content: space-between;
`
const Group = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const Seg = styled.button<{ $on: boolean }>`
  all: unset;
  box-sizing: border-box;
  min-width: 44px;
  min-height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme, $on }) => ($on ? theme.colors.invertedContrast : theme.colors.textSubtle)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primary : 'transparent')};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`
const Chip = styled.a<{ $tone: 'ok' | 'partial' | 'unavailable' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme, $tone }) => ($tone === 'ok' ? theme.colors.success : $tone === 'partial' ? theme.colors.warning : theme.colors.failure)};
  border: 1px solid currentColor;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
  &:hover {
    text-decoration: underline;
  }
`
const Explain = styled.details`
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
  max-width: 72ch;
  summary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    cursor: pointer;
    list-style: none;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSubtle};
    &::-webkit-details-marker {
      display: none;
    }
    &::before {
      content: '';
      width: 0;
      height: 0;
      border-left: 5px solid currentColor;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      transition: transform 120ms ease-out;
    }
    &:hover {
      color: ${({ theme }) => theme.colors.text};
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
  &[open] summary::before {
    transform: rotate(90deg);
  }
  p {
    margin: 4px 0 0 11px;
  }
`
const LeftOutBox = styled(Explain)`
  margin-top: 8px;
  ul {
    margin: 4px 0 0 11px;
    padding: 0;
    list-style: none;
  }
  li {
    margin: 6px 0;
    overflow-wrap: anywhere;
  }
  li a {
    font-weight: 600;
  }
`
const Cards = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 12px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const feeLabel = (fee?: number) => (fee ? ` ${Number((fee / 10000).toFixed(4))}%` : '')

/** One line "N pools left out"; open, each pool with the reason it is not in the sum (largest on-chain figure first). */
function LeftOutList({ kind, series }: { kind: 'tvl' | 'volume'; series: SeriesOut }) {
  const { t } = useTranslation()
  const list = series.leftOut ?? []
  if (!list.length || (series.total ?? 0) < 2) return null
  return (
    <LeftOutBox data-testid={`ih-${kind}-leftout`}>
      <summary>
        {list.length === 1 ? t('1 pool left out') : t('%n% pools left out', { n: list.length })}
        {series.included ? ` · ${t('the chart is the other %n%', { n: series.included })}` : ''}
      </summary>
      <ul>
        {list.map((p) => (
          <li key={`${p.version}:${p.address}`} data-testid={`ih-${kind}-leftout-item`}>
            <NextLinkFromReactRouter to={`/info/cadinu/pools/${p.address}?v=${p.version}`}>
              {p.pair} {p.version.toUpperCase()}
              {feeLabel(p.feeTier)}
            </NextLinkFromReactRouter>
            : {p.reason}
            {/* the on-chain figure only where the reason does not already name it */}
            {p.chainUSD && p.chainUSD >= 0.01 && !/the chain has/.test(p.reason) ? ` (${t('on-chain now')} $${formatAmount(p.chainUSD)})` : ''}
          </li>
        ))}
      </ul>
    </LeftOutBox>
  )
}

function SeriesCard({ title, series, kind, loading, onExplain }: { title: string; series?: SeriesOut; kind: 'tvl' | 'volume'; loading: boolean; onExplain: () => void }) {
  const { t } = useTranslation()
  const [hover, setHover] = useState<number | undefined>()
  const [hoverDate, setHoverDate] = useState<string | undefined>()
  const data = useMemo(() => (series?.points ?? []).map((p) => ({ time: fromUnixTime(p.date), value: p.value })), [series])
  const last = series?.points?.length ? series.points[series.points.length - 1].value : undefined
  const total = useMemo(() => (series?.points ?? []).reduce((s, p) => s + p.value, 0), [series])
  /**
   * Site review 59 #7 (2026-10-08): with pools left out, the TVL card's big number is the on-chain figure (the pool reserves read at the
   * latest block, priced exactly as the Liquidity tile above, so the two agree), and the chart's last point, a lagging day value of the
   * verified subset, moves to the subline. Volume shows the total of the range (what the bars add up to); a full TVL series its latest value.
   */
  const chainHeadline = kind === 'tvl' && series?.state === 'partial' && series.chainNow !== null && series.chainNow !== undefined ? series.chainNow : undefined
  const headline = hover ?? (kind === 'volume' ? total : chainHeadline ?? last)
  const drawn = series?.state === 'ok' || series?.state === 'partial'
  const share = series?.coverage !== undefined ? `${Math.round(series.coverage * 100)}%` : ''
  return (
    <Card data-testid={`ih-${kind}`} data-state={loading ? 'loading' : series?.state ?? 'error'}>
      <Box p={['16px', '16px', '24px']}>
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
          <Text bold color="secondary">{title}</Text>
          {!loading && series && (
            <Chip href="#chain-check" onClick={onExplain} $tone={series.state === 'ok' ? 'ok' : series.state === 'partial' ? 'partial' : 'unavailable'} data-testid={`ih-${kind}-status`}>
              {series.state === 'ok' ? t('Checked against the chain') : series.state === 'partial' ? `${t('Verified pools only')} · ${share} ${t('of')} ${kind === 'tvl' ? 'TVL' : t('volume')}` : t('Not verified')}
            </Chip>
          )}
        </Flex>
        {loading ? (
          <>
            <Skeleton width="128px" height="36px" my="4px" />
            <Skeleton width="100%" height="250px" />
          </>
        ) : drawn ? (
          <>
            <Text bold fontSize="24px" data-testid={`ih-${kind}-value`}>${formatAmount(headline ?? 0)}</Text>
            <Text fontSize="13px" color="textSubtle" data-testid={`ih-${kind}-subline`}>
              {hoverDate ??
                (kind === 'volume'
                  ? t('Total for the selected range')
                  : chainHeadline !== undefined
                  ? `${t('On-chain now')} · ${t('chart, latest day')}: $${formatAmount(last ?? 0)}`
                  : t('Latest day'))}
              {series?.state === 'partial' && hoverDate === undefined ? ` · ${t('verified pools only')}` : ''}
            </Text>
            <Box height="250px" mt="8px" data-testid={`ih-${kind}-chart`} data-points={series.points.length}>
              {kind === 'tvl' ? (
                <LineChart data={data} setHoverValue={setHover} setHoverDate={setHoverDate} />
              ) : (
                <BarChart data={data} setHoverValue={setHover} setHoverDate={setHoverDate} />
              )}
            </Box>
            <Text fontSize="12px" color="textSubtle" mt="8px" data-testid={`ih-${kind}-method`}>
              {series!.state === 'ok' ? '✓ ' : '◐ '}
              {series!.state === 'ok' ? t('Checked against the chain') : t('Partial')}: {series!.method}
            </Text>
            <LeftOutList kind={kind} series={series!} />
          </>
        ) : (
          <Flex flexDirection="column" style={{ minHeight: 250, gap: 8 }} justifyContent="center" data-testid={`ih-${kind}-unavailable`}>
            <Text bold fontSize="18px">{t('Not available for this selection')}</Text>
            <Text color="textSubtle" fontSize="14px">
              {series?.reason ?? t('The history could not be loaded.')}
            </Text>
            {series?.chainNow !== null && series?.chainNow !== undefined && (
              <Text fontSize="14px">
                {t('On-chain now')}: <b data-testid={`ih-${kind}-chainnow`}>${formatAmount(series.chainNow)}</b>
              </Text>
            )}
            <Text fontSize="12px" color="textSubtle">
              {t('We show a chart only when it matches the chain. A number we cannot confirm is not drawn.')}
            </Text>
            {series && <LeftOutList kind={kind} series={series} />}
          </Flex>
        )}
      </Box>
    </Card>
  )
}

export function History({ scope, id, version, canPickProtocol = true }: { scope: 'protocol' | 'pool' | 'token'; id?: string; version: DexVersion | 'all'; canPickProtocol?: boolean }) {
  const { t } = useTranslation()
  const [range, setRange] = useState<Range>('1m')
  const [protocol, setProtocol] = useState<DexVersion | 'all'>(version)
  const [explain, setExplain] = useState(false)
  const openExplain = () => setExplain(true)
  const v = canPickProtocol ? protocol : version
  const key = `/api/info/history?version=${v}&scope=${scope}&range=${range}${id ? `&id=${id}` : ''}`
  const { data, error, isLoading } = useSWR<HistoryOut>(key, (u: string) => fetch(u).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status))))), {
    refreshInterval: 120_000,
    revalidateOnFocus: false,
    keepPreviousData: true,
    shouldRetryOnError: false,
  })
  return (
    <Box data-testid="ih-root">
      <Bar>
        <Group role="tablist" aria-label={t('Time range')} data-testid="ih-ranges">
          {RANGES.map(([k, label]) => (
            <Seg key={k} role="tab" aria-selected={range === k} $on={range === k} onClick={() => setRange(k)} data-testid={`ih-range-${k}`}>{label}</Seg>
          ))}
        </Group>
        {canPickProtocol && (
          <Group role="tablist" aria-label={t('Protocol')} data-testid="ih-protocols">
            {PROTOCOLS.map(([k, label]) => (
              <Seg key={k} role="tab" aria-selected={protocol === k} $on={protocol === k} onClick={() => setProtocol(k)} data-testid={`ih-protocol-${k}`}>{label}</Seg>
            ))}
          </Group>
        )}
      </Bar>
      <Cards>
        <SeriesCard title={t('TVL')} kind="tvl" series={error ? undefined : data?.tvl} loading={isLoading && !data} onExplain={openExplain} />
        <SeriesCard title={t('Volume')} kind="volume" series={error ? undefined : data?.volume} loading={isLoading && !data} onExplain={openExplain} />
      </Cards>
      <Explain id="chain-check" data-testid="ih-chain-check" open={explain} onToggle={(e) => setExplain((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>{t('How these charts are checked')}</summary>
        <p>
        {t('Every pool series is compared with the chain before it is drawn: TVL against the pool\'s reserves or balances now, volume against the last 7 days. A chart is the sum of the pools that pass. It is marked')}{' '}
        <b>{t('checked')}</b> {t('when every pool of the selection passes, and')} <b>{t('verified pools only')}</b>{' '}
        {t('when some do not: the share of the on-chain figure the chart holds is stated, and "N pools left out" lists each missing pool with its reason.')}{' '}
        <b>{t('Not verified')}</b> {t('means no pool passed.')}
        </p>
        <p>
        {t('Prices: a stablecoin side counts $1; a WBNB side counts BNB/USD at the close of each day, read on-chain from the PancakeSwap V2 WBNB/USDT pair (today: the live price). A pool with neither side priced has no USD history. Nothing is scaled, patched or interpolated; a pool that fails appears as soon as its source agrees with the chain.')}
        </p>
      </Explain>
    </Box>
  )
}

export default History
